import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  ListItem,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import PlaceIcon from '@mui/icons-material/Place';
import { useTranslation } from 'react-i18next';
import { AccountData } from '../../api';
import { useLanguage } from '../GeneralUtils';
import { getActiveLocations } from './locationGrouping';

type Props = {
  account: AccountData;
  selectedLocationIds: string[] | null;
  onChange: (next: string[] | null) => void;
};

export const LocationsFilterSection = ({ account, selectedLocationIds, onChange }: Props) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { t } = useTranslation();
  const language = useLanguage();

  const activeLocations = getActiveLocations(account);
  if (activeLocations.length <= 1) return null;

  const allIds = activeLocations.map((l) => l.id);
  const currentIds = selectedLocationIds ?? allIds;

  const setCurrentIds = (next: string[]) => {
    onChange(next.length === allIds.length ? null : next);
  };

  const toggle = (locationId: string, checked: boolean) => {
    const next = checked ? [...new Set([...currentIds, locationId])] : currentIds.filter((id) => id !== locationId);
    setCurrentIds(next);
  };

  const allSelected = currentIds.length === allIds.length;
  const toggleAll = () => {
    onChange(allSelected ? [] : null);
  };

  return (
    <>
      <ListItem sx={{ pb: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <Typography variant="overline" color="text.secondary">
            {t('calendar.filter.locations')}
          </Typography>
          {activeLocations.length > 1 && (
            <Button
              size="small"
              onClick={toggleAll}
              sx={{ textTransform: 'none', py: 0, minWidth: 0, color: 'text.secondary' }}>
              <Typography variant="caption">
                {allSelected ? t('calendar.filter.deselect_all') : t('calendar.filter.select_all')}
              </Typography>
            </Button>
          )}
        </Box>
      </ListItem>
      <Grid container mt={-1}>
        {activeLocations.map((loc) => (
          <ListItem key={loc.id} disablePadding sx={{ paddingInlineStart: '8px' }}>
            <FormControlLabel
              sx={{ width: '100%', mx: 0, py: isDesktop ? 0 : 0.5 }}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PlaceIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  {loc.name?.[language] ?? loc.name?.en ?? ''}
                </Box>
              }
              control={
                <Checkbox
                  size={isDesktop ? 'small' : 'medium'}
                  checked={currentIds.includes(loc.id)}
                  onChange={(e) => toggle(loc.id, e.target.checked)}
                />
              }
            />
          </ListItem>
        ))}
      </Grid>
      <Divider />
    </>
  );
};
